import * as styles from "./KnobControl.css";

import { clsx } from "clsx";
import { useCallback, useRef } from "react";
import type { JSX, KeyboardEvent, PointerEvent } from "react";

import { useKnobContext } from "./Knob.context";
import type { KnobControlProps } from "./Knob.types";

function angleFromPoint(
  clientX: number,
  clientY: number,
  rect: DOMRect,
): number {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const dx = clientX - centerX;
  const dy = clientY - centerY;

  let degrees = (Math.atan2(dy, dx) * 180) / Math.PI + 90;

  if (degrees < 0) {
    degrees += 360;
  }

  return degrees;
}

export function KnobControl({
  className,
  children,
}: KnobControlProps): JSX.Element {
  const {
    min,
    max,
    step,
    value,
    disabled,
    labelId,
    boxSize,
    controlRef,
    setValue,
  } = useKnobContext();

  const draggingRef = useRef(false);

  const valueFromAngle = useCallback(
    (degrees: number) => min + (degrees / 360) * (max - min),
    [min, max],
  );

  const updateFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const el = controlRef.current;

      if (!el) {
        return;
      }

      const degrees = angleFromPoint(
        clientX,
        clientY,
        el.getBoundingClientRect(),
      );

      setValue(valueFromAngle(degrees));
    },
    [controlRef, setValue, valueFromAngle],
  );

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    draggingRef.current = true;
    controlRef.current?.focus();
    updateFromPointer(event.clientX, event.clientY);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) {
      return;
    }

    updateFromPointer(event.clientX, event.clientY);
  };

  const stopDragging = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    draggingRef.current = false;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    const bigStep = step * 10;

    const nextValueByKey: Record<string, number> = {
      ArrowUp: value + step,
      ArrowRight: value + step,
      ArrowDown: value - step,
      ArrowLeft: value - step,
      PageUp: value + bigStep,
      PageDown: value - bigStep,
      Home: min,
      End: max,
    };

    const nextValue = nextValueByKey[event.key];

    if (nextValue !== undefined) {
      event.preventDefault();
      setValue(nextValue);
    }
  };

  return (
    <div
      ref={controlRef}
      data-slot="knob-control"
      data-disabled={disabled || undefined}
      className={clsx(styles.control, className)}
      style={{ width: boxSize, height: boxSize }}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-labelledby={labelId || undefined}
      aria-disabled={disabled || undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
}
